import {
  ApplicationConfig,
  provideBrowserGlobalErrorListeners,
  isDevMode,
  Injector,
  APP_INITIALIZER,
} from '@angular/core';
import { provideRouter, RouteReuseStrategy } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideEnvironmentNgxMask } from 'ngx-mask';
import { createCustomElement } from '@angular/elements';

/** config angular i18n **/
import { registerLocaleData } from '@angular/common';
import en from '@angular/common/locales/en';
registerLocaleData(en);

/** config ng-zorro-antd i18n **/
import { provideNzI18n, en_US } from 'ng-zorro-antd/i18n';

import { appRoutes } from './app.routes';
import { RapiderRouteReuseStrategy } from './rapider-route-reuse-strategy';
// eslint-disable-next-line @nx/enforce-module-boundaries
import {
  authInterceptor,
  provideAuthInitialization,
} from '@rapider/authentication';

import { provideRapiderMonaco } from '@rapider/monaco-code-editor';
import { provideRapiderMarkdown } from '@rapider/markdown-preview';
// Adjust the path to wherever you placed your newly created RapiderChartComponent wrapper
import { RapiderChartComponent } from '@rapider/template-renderer';
import { RapiderSelectComponent } from '@rapider/template-renderer';
import { RapiderTooltipComponent } from '@rapider/template-renderer';

import { environment } from '@rapider/environments';
import { provideApiConfiguration } from '@rapider/sdk';

/**
 * Factory function to register Angular components as Custom Web Elements.
 * This allows complex components (like ng2-charts) to be safely rendered inside DOMPurify sanitized HTML via Handlebars.
 *
 * @param {Injector} injector - The Angular dependency injector.
 * @returns {() => void} A function executed during app initialization to define the web components.
 */
export function registerCustomElements(injector: Injector) {
  return () => {
    if (typeof customElements !== 'undefined') {
      // Chart component
      if (!customElements.get('rapider-chart')) {
        const ChartElement = createCustomElement(RapiderChartComponent, {
          injector,
        });
        customElements.define('rapider-chart', ChartElement);
      }
      // Select component
      if (!customElements.get('rapider-select')) {
        const SelectElement = createCustomElement(RapiderSelectComponent, {
          injector,
        });
        customElements.define('rapider-select', SelectElement);
      }
      // Tooltip component
      if (!customElements.get('rapider-tooltip')) {
        const TooltipElement = createCustomElement(RapiderTooltipComponent, {
          injector,
        });
        customElements.define('rapider-tooltip', TooltipElement);
      }
    }
  };
}

/**
 * Root application configuration.
 * Configures routing, state management (NgRx), and HTTP interceptors.
 */
export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideRouter(appRoutes),
    { provide: RouteReuseStrategy, useClass: RapiderRouteReuseStrategy },
    provideNzI18n(en_US),

    /* Initialization hooks - Check tokens and rebuild auth state on refresh via APP_INITIALIZER */
    provideAuthInitialization(),

    provideRapiderMonaco(),
    provideRapiderMarkdown(),
    provideEnvironmentNgxMask(),

    /* Modern HTTP Client configuration with functional interceptors */
    provideHttpClient(withInterceptors([authInterceptor])),

    provideApiConfiguration(environment.apiBaseUrl),

    /* Legacy global NgRx configuration (Keep for now until fully migrated to SignalStore) */
    provideStore(),
    provideEffects(),
    provideStoreDevtools({ maxAge: 25, logOnly: !isDevMode() }),

    /* Register Custom Web Elements (e.g., RapiderChartComponent) during application boot */
    {
      provide: APP_INITIALIZER,
      useFactory: registerCustomElements,
      deps: [Injector],
      multi: true,
    },
  ],
};
